import CompanyLink from "@/components/common/CompanyLink";
import CompanyLogo from "@/components/common/CompanyLogo";
import SourceTag from "@/components/common/SourceTag";
import { CheckIcon, PinIcon } from "@/components/icons";
import type { Posting } from "@/lib/api/catalogue";
import { dayOf, payLine, postedDay, sentPhrase, type Sent, type StatusChip } from "@/lib/posting";
import { FactPill, StatusPill } from "./parts";

/** The title, the company and where, and the pills: where it stands, and its dates. */
export default function PostingHeading({
    posting,
    applied,
    sent,
    chip,
    member,
    seen,
    timeZone,
}: {
    posting: Posting;
    applied: boolean;
    sent: Sent | null;
    chip: StatusChip;
    member: boolean;
    /** The last pill: when it closed, or when it was last seen. */
    seen: string;
    /** The zone the days are told in; the person's own when undefined. */
    timeZone?: string;
}) {
    return (
        <div className="flex min-w-0 flex-[1_1_420px] flex-col gap-2.5">
            <h1 className="m-0 font-display text-page-title font-bold tracking-[-0.025em] [text-wrap:balance]">
                {posting.title}
            </h1>
            <div className="flex flex-wrap items-center gap-2.5 text-ui">
                <span className="inline-flex items-center gap-2.5 text-subhead font-semibold">
                    <CompanyLogo url={posting.logo_url ?? null} company={posting.company} />
                    <CompanyLink
                        company={posting.company}
                        logoUrl={posting.logo_url ?? null}
                        source={posting.source}
                        board={posting.board}
                        from="PostingHeading"
                    />
                </span>
                <SourceTag source={posting.source} size="md" />
                {posting.location && (
                    <span className="inline-flex items-center gap-1 text-fg-muted">
                        <PinIcon className="size-[13px] text-fg-faint" />
                        {posting.location}
                    </span>
                )}
                {applied && payLine(posting) && (
                    <span className="text-fg-muted">{payLine(posting)}</span>
                )}
            </div>
            <div className="flex flex-wrap gap-1.5">
                {sent ? (
                    <StatusPill label={`Applied ${sentPhrase(sent)}`} tone="applied">
                        <CheckIcon className="size-3" strokeWidth={2.8} />
                    </StatusPill>
                ) : (
                    member && <StatusPill label={chip.label} tone={chip.tone} />
                )}
                {posting.posted_at && (
                    <FactPill>Posted {postedDay(posting.posted_at, timeZone)}</FactPill>
                )}
                <FactPill>Found {dayOf(posting.first_seen_at, timeZone)}</FactPill>
                <FactPill>{seen}</FactPill>
            </div>
        </div>
    );
}
